import { useCallback, useRef, useState } from "react"
import { services } from "@/services"
import type { NetworkQuery, NetworkUnit } from "@/services/types"
import { useResource } from "@/shared/hooks/useResource"
import { isCompletePostalCode } from "@/shared/lib/postalCode"

export interface NetworkFilters {
  postalCode: string
  state: string
  city: string
}

const EMPTY_FILTERS: NetworkFilters = { postalCode: "", state: "", city: "" }

/**
 * Estado da consulta a rede credenciada.
 *
 * A busca e explicita: os filtros mudam livremente enquanto o usuario digita, e
 * so o submit promove os valores atuais a consulta. Buscar a cada tecla faria
 * uma requisicao por caractere assim que a fonte virar API.
 */
export function useNetworkSearch() {
  const [filters, setFilters] = useState<NetworkFilters>(EMPTY_FILTERS)
  const [query, setQuery] = useState<NetworkQuery | null>(null)
  const [formError, setFormError] = useState("")
  const resultsRef = useRef<HTMLDivElement>(null)

  const loadResults = useCallback(async (): Promise<NetworkUnit[]> => {
    if (!query) return []
    return services.network.searchUnits(query)
  }, [query])

  const results = useResource<NetworkUnit[]>(loadResults)

  const canSearch =
    isCompletePostalCode(filters.postalCode) || filters.state !== "" || filters.city.trim() !== ""

  const focusResults = () => {
    // O timeout espera o React pintar a lista: sem ele o scroll acontece contra
    // o layout antigo e para no lugar errado.
    window.setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100)
  }

  const search = () => {
    if (!canSearch) {
      setFormError("Informe ao menos o CEP, o estado ou a cidade para consultar.")
      return
    }

    setFormError("")
    setQuery({
      postalCode: filters.postalCode,
      state: filters.state,
      city: filters.city,
    })
    focusResults()
  }

  /** Clique num estado do mapa: substitui os filtros e busca de uma vez. */
  const searchByState = (state: string) => {
    setFilters({ ...EMPTY_FILTERS, state })
    setFormError("")
    setQuery({ state })
    focusResults()
  }

  const clear = () => {
    setFilters(EMPTY_FILTERS)
    setFormError("")
    setQuery(null)
  }

  return {
    filters,
    setFilters,
    formError,
    canSearch,
    /** `null` enquanto nenhuma busca foi feita — estado inicial vs. sem resultados. */
    query,
    results,
    resultsRef,
    search,
    searchByState,
    clear,
  }
}
